import React from 'react'
import { HeroIcons } from '../../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'

type FooterPaginationProps = {
  currentPage: number
  maxPages: number | undefined
  maxResults: number | undefined
  onPageChange: (page: number) => void
  isLoading: boolean
}

export default function FooterPagination({
  currentPage,
  maxPages,
  maxResults,
  onPageChange,
  isLoading,
}: FooterPaginationProps) {
  const { t } = useTranslation('common')

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (maxPages || 1)) {
      onPageChange(page)
    }
  }

  const renderPaginationLinks = (maxDisplayedPages: number) => {
    const paginationLinks: any[] = []
    const totalPages = maxPages || 1

    const renderPageLink = (page: number) => {
      const isCurrentPage = currentPage === page
      const linkClassName = isCurrentPage
        ? 'relative z-10 inline-flex items-center bg-indigo-600 px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
        : 'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'

      paginationLinks.push(
        <button
          key={page}
          className={linkClassName}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </button>,
      )
    }

    const renderEllipsis = () => {
      paginationLinks.push(
        <span
          key="ellipsis"
          className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900"
        >
          ...
        </span>,
      )
    }

    if (totalPages <= maxDisplayedPages) {
      for (let page = 1; page <= totalPages; page++) {
        renderPageLink(page)
      }
    } else {
      let startPage = Math.max(
        1,
        currentPage - Math.floor(maxDisplayedPages / 2),
      )
      let endPage = Math.min(totalPages, startPage + maxDisplayedPages - 1)

      if (
        currentPage > maxDisplayedPages &&
        endPage - startPage <= maxDisplayedPages
      ) {
        startPage = endPage - maxDisplayedPages + 1
      }

      for (let page = startPage; page <= endPage; page++) {
        renderPageLink(page)
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          renderEllipsis()
        }
        renderPageLink(totalPages)
      }
    }

    return paginationLinks
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white pt-3">
        <div className="mx-auto flex items-center">
          {t('pagination.footer.loading')}
          <HeroIcons.ArrowPathIcon className="ml-2 h-4 w-4 animate-spin" />
        </div>
      </div>
    )
  }

  if (maxResults === 0) {
    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white pb-3 pt-3 text-gray-500">
        <div className="mx-auto flex items-center">
          {t('pagination.footer.noPurchaseFound')}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-grow items-center justify-between border-t border-gray-200 bg-white pt-3">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-2 text-gray-700 hover:bg-gray-50"
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <HeroIcons.ChevronLeftIcon
            className="h-3 w-auto"
            aria-hidden="true"
          />
        </button>
        {renderPaginationLinks(3)}
        <button
          className="py-2bg-white relative ml-3 inline-flex items-center rounded-md border border-gray-300 px-2 text-gray-700 hover:bg-gray-50"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          <HeroIcons.ChevronRightIcon
            className="h-3 w-auto"
            aria-hidden="true"
          />
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            {t('pagination.footer.results')}{' '}
            <span className="font-medium">{maxResults}</span>
          </p>
        </div>
        <div>
          <nav
            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
            aria-label="Pagination"
          >
            <button
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <HeroIcons.ChevronLeftIcon
                className="h-5 w-5"
                aria-hidden="true"
              />
            </button>
            {renderPaginationLinks(6)}
            <button
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <HeroIcons.ChevronRightIcon
                className="h-5 w-5"
                aria-hidden="true"
              />
            </button>
          </nav>
        </div>
      </div>
    </div>
  )
}
